"use client"

import { Suspense, useEffect, useRef, useState } from "react"
import { Canvas, useFrame } from "@react-three/fiber"
import { Stars } from "@react-three/drei"
import type { Points } from "three"

interface GalaxyStarsBackgroundProps {
  /** "page" = fixed full viewport (pathway). "section" = absolute fill within a relative parent. */
  variant?: "page" | "section"
  /** When true, Three.js stars render only at md+ (768px); gradient still shows on mobile. */
  desktopOnlyStars?: boolean
}

/** Calm galaxy-style starfield: same density/look as /galaxy at rest, with a slow drift. Non-interactive. */
function CalmStars() {
  const starsRef = useRef<Points>(null)

  useFrame((_, delta) => {
    if (!starsRef.current) return
    // Slow background drift — not travel warp, not user-controlled
    starsRef.current.rotation.y += delta * 0.015
    starsRef.current.rotation.x += delta * 0.004
  })

  return (
    <Stars
      ref={starsRef}
      radius={100}
      depth={50}
      count={5000}
      factor={4}
      saturation={0}
      fade
      speed={1}
    />
  )
}

export function GalaxyStarsBackground({
  variant = "page",
  desktopOnlyStars = false,
}: GalaxyStarsBackgroundProps = {}) {
  const [showStars, setShowStars] = useState(!desktopOnlyStars)

  useEffect(() => {
    if (!desktopOnlyStars) return

    const mq = window.matchMedia("(min-width: 768px)")
    const update = () => setShowStars(mq.matches)

    update()
    mq.addEventListener("change", update)
    return () => mq.removeEventListener("change", update)
  }, [desktopOnlyStars])

  const wrapperClass =
    variant === "page"
      ? "fixed inset-0 -z-10 pointer-events-none"
      : "absolute inset-0 pointer-events-none"

  return (
    <div className={wrapperClass} aria-hidden>
      {/* Same diagonal as /galaxy, slightly darker corners; cyan mid kept */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#04033F] via-[#01ADEF] to-[#04033F]" />
      <div className="absolute inset-0 bg-black/20" />
      {showStars && (
        <Canvas
          camera={{
            position: [0, 0, 0],
            fov: 75,
            near: 0.1,
            far: 1000,
          }}
          gl={{ alpha: true, antialias: true }}
          style={{ pointerEvents: "none" }}
          onCreated={({ gl }) => {
            gl.setClearColor(0x000000, 0)
          }}
        >
          <Suspense fallback={null}>
            <ambientLight intensity={0.4} />
            <CalmStars />
          </Suspense>
        </Canvas>
      )}
    </div>
  )
}
